import React, {useMemo} from 'react'
import moment, {Moment, MomentInput} from 'moment'
import {DatePicker as MUIDatePicker, LocalizationProvider, PickerChangeHandlerContext, DateValidationError} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {DateFormats} from "../../../constants/securspace-constants";

export interface IDatePicker {
  name?: string,
  /** identifies field name to users & screen readers */
  label?: string,
  value?: MomentInput,
  /** Callback fired when the date value changes. Receives a Moment object or null. */
  onChange?: (date: Moment | null) => void,
  /** prevent future dates from being selected if true (default is false)*/
  disableFuture?: boolean,
  /** set the minimum date that can be selected*/
  minDate?: MomentInput,
  /** set the maximum date that can be selected*/
  maxDate?: MomentInput,
  dateFormat?: string,
  /** any other props for MUI DatePicker */
  [key: string]: any,
}

/**
 * DatePicker component that wraps MUI DatePicker with Moment.js support and additional features
 *
 * @param name
 * @param label
 * @param value
 * @param onChange
 * @param disableFuture
 * @param minDate
 * @param maxDate
 * @param dateFormat The format string to parse and display the date.
 * @param props
 */

const DatePicker = ({
                      name = "",
                      label = 'Default Label',
                      value,
                      onChange = () => {},
                      disableFuture = undefined,
                      minDate = moment('01/01/1901', DateFormats.DAY),
                      maxDate = undefined,
                      dateFormat = DateFormats.DAY,
                      ...props
                    }: IDatePicker) => {

  const dateValue = useMemo(() => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      // Prefer the configured dateFormat first (e.g., MM/DD/YYYY), then fall back to strict ISO_8601
      // This order ensures we parse expected localized formats before attempting ISO-only inputs
      const parsedWithFormat = moment(value, dateFormat, true); // strict mode
      if (parsedWithFormat.isValid()) {
        return parsedWithFormat;
      }

      // Try ISO format for ISO strings like "2026-01-13T09:00:00.000Z"
      const parsedISO = moment(value, moment.ISO_8601, true);
      if (parsedISO.isValid()) {
        return parsedISO;
      }

      // If neither strict parse succeeded, treat as invalid
      return null;
    }

    // Non-string MomentInput values (Date, number, Moment, etc.)
    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate : null;
  }, [value, dateFormat]);

  const handleChange = (dateMoment: Moment | null, context: PickerChangeHandlerContext<DateValidationError>) => {
    // validationError is null when valid, or a string like "invalidDate", "minDate", "maxDate" when invalid
    if (context?.validationError) {
      // Don't pass invalid dates to parent
      return;
    }
    onChange?.(dateMoment);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MUIDatePicker
        name={name}
        label={label}
        slotProps={{textField: {variant: "standard"}}}
        value={dateValue}
        onChange={handleChange}
        format={dateFormat}
        disableFuture={disableFuture}
        minDate={minDate ? moment(minDate) : undefined}
        maxDate={maxDate ? moment(maxDate) : undefined}
        sx={{
          width: '100%',
        }}
        {...props}
      />
    </LocalizationProvider>
  )
}

export default DatePicker
